import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import Box from '@mui/material/Box';
import { Record, List, Set, is } from 'immutable';
import { generateJETableStoreKey, matchCell } from 'domain/journal/helper';
import { resolveOriginalCell } from 'domain/journal/fieldRearrangementResolve';
import { createMemoFn, createMemoPropFn } from 'lib/propHelpers';
import {
  journalSelector,
  gridEntriesSelector,
  settingsEntriesSelector,
  rearrangedSettingsSelector,
  updateJournalEntryAction,
  polygonsSelector,
  createReferenceAction,
  setRefernceAction,
  activeReferenceSelector,
  extraPolygonSelector,
  referenceSelector,
  insertGridAction,
  updateJournalEntriesAction,
  jeTypeSelector,
  jeInvoiceTypeSelector,
  initialHiddenColumnsSelector,
  getJournalEntryPageAction,
  lineItemsSelector,
  lineCountSelector,
  linesSelector,
  settingsSelector,
  selectRowAction,
  selectedRowSelector,
  reconcileColumnSelector,
  notHiddenColumnsSelector,
  pinnedColumnSelector,
  amountColumnSelector,
  rowMessagesSelector,
  errorFromErpSelector,
  isJeLayoutRTLSelector,
  journalSettingsDisabledColsSelector,
  journalSettingsPinnedColsSelector,
  getJELineColumnsVisibilityAction,
  getJournalSettingsPinnedColsAction,
  updateJournalSettingsPinnedColsAction,
  updateJELineColumnsVisibilityAction,
  getCachedPaginationListSelector,
  jeGridLineColumnsDictionarySelector,
  updateJournalSettingsOrderedColsAction,
  journalSettingsOrderedColsSelector,
  getJournalSettingsOrderedColsAction,
  setLoadingAction,
  endLoadingAction,
  isJELoadingSelector,
  setEditingAction,
  endEditingAction,
  isJEEditingSelector,
  selectUnmatchedLinesAction,
} from 'domain/journal';
import {
  documentStatusesSelector,
  documentHotkeysSelector,
  textractEnabledForDocumentSelector,
} from 'domain/documents';
import { isChangeProps } from 'lib/helpers';
import { showRowDetailsAction, showReconciliationRequestDetailsAction } from 'domain/reconciliation/actions';
import { currentConnectedERPselector } from 'domain/settings';
import { rtlEnable, documentViewSelector, userGUIDSelector } from 'domain/env';
import { getLoadPagesByRows, zipQueryParams } from './layout/JEVirtualLinesTable/helpers';
import {
  getMeta,
  getMetaRange,
  getNext,
  checkCallDataSupport,
  keyCodeArrow,
  removeLine,
  OptionItemFactory,
  range,
  isStatement,
  createPolygonReferens,
  createMultiPolygonRefernce,
} from './helpers';
import _ from 'lodash';
import { FormattedMessage } from 'react-intl';
import { hotkeyMatchAdapter } from 'domain/documents/adapters';

import JEManualSummaryTable from 'components/Tables/layout/JEManualSummaryTable';
import JEHeadersTable from 'components/Tables/layout/JEHeadersTable';
import JESummaryTable from 'components/Tables/layout/JESummaryTable';
import JELinesTable from 'components/Tables/layout/JELinesTable';
import JEVirtualLinesTable from 'components/Tables/layout/JEVirtualLinesTable';
import JEProcessingOverlay from './JEProcessingOverlay';
import Cell from 'components/Tables/Cells/Cell';
import DescriptionCell from 'components/Tables/Cells/DescriptionCell';
import SelectAllCheckbox from 'components/Tables/grid/SelectAllCheckbox';
import SelectUnmatchedLinesCheckbox from 'components/Tables/grid/SelectUnmatchedLinesCheckbox';
import JEError from './JEError';
import elements from 'components/elements';
import LineHotkeyCatcher from 'components/Tables/LineHotkeyCatcher';
import OptionalLineHotkeyCatcher from 'components/Tables/OptionalLineHotkeyCatcher';
import DocumentHotkeyCatcher from 'components/Tables/DocumentHotkeyCatcher';
import { JERoot, JELinesSelectAll, JELoader } from 'components/Tables/StyledComponents';
import CircularProgress from '@mui/material/CircularProgress';

import { orderByMap } from 'lib/array';

class Tables extends React.Component {
  refVisible;

  constructor(props) {
    super(props);
    this.state = {
      active: null,
      // saga loads first page, see here: src/pages/document/sagas.js
      // yield call(Journal.ensureJounalEntry, params.documentId, false, 1, 50);
      loadedVgridPages: [1],
      // it is a temporary duplicate of value in the global store while order update,
      // it is necessary to avoid blinking after sorting
      cacheOrder: null,
    };
  }

  componentDidMount() {
    this.setInitialFocus();
  }

  componentDidUpdate(prevProps) {
    const {
      jeType,
      orderedColumns,
      data,
      settingsData,
      getJournalSettingsPinnedCols,
      getJELineColumnsVisibility,
      getJournalSettingsOrderedCols,
      initialHiddenColumns,
    } = this.props;
    const { active, cacheOrder } = this.state;
    // Check both raw journal data and rearranged settings data for the active cell.
    // When field rearrangement moves a cell to a new address (e.g. B2→D8),
    // the raw journal data won't have the target address, but the rearranged
    // settings data will. Without this check, the active cell is considered
    // missing and focus jumps to the next cell, closing any open dropdown.
    const cellExistsInData = !!(data.get(active) || (settingsData && settingsData.get(active)));
    if (typeof active === 'string' && data.size > 0 && !cellExistsInData && !isStatement(jeType)) {
      compose(this.handleActive, this.getEnterNextCell)(active);
    }

    if (prevProps.orderedColumns !== orderedColumns && cacheOrder) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ cacheOrder: null });
    }

    // determine when to recalculate columns that have been changed(not equal - recalculate columns state)
    const isEqualInitialHiddenColumns = is(prevProps.initialHiddenColumns, initialHiddenColumns);

    if (isChangeProps(prevProps, this.props, ['userGUID', 'jeInvoiceType', 'erp']) || !isEqualInitialHiddenColumns) {
      getJournalSettingsPinnedCols();
      getJELineColumnsVisibility();
      getJournalSettingsOrderedCols();
    }
  }

  onChangeSelectedRows = (selectedRows) => {
    const { selectRow } = this.props;
    selectRow(selectedRows);
  };

  onSelectUnmatchedLines = (checked) => {
    const { selectUnmatchedLines } = this.props;
    selectUnmatchedLines(checked);
  };

  onUpdateOrderAfterChangePinnedCols = (pinnedCols) => {
    const { journalSettingsPinnedCols } = this.props;
    const orderedColumns = this.getCurrentOrder();

    const isPinned = pinnedCols.length > journalSettingsPinnedCols.length;
    const [items, compare] = isPinned
      ? [pinnedCols, journalSettingsPinnedCols]
      : [journalSettingsPinnedCols, pinnedCols];

    // Find ALL differences, not just the first one
    const diffColls = items.filter((i) => !compare.includes(i));

    // Process all differences using reduce instead of forEach with let
    const newOrderedColumns = diffColls.reduce(
      (acc, diffColl) =>
        acc
          .update(
            'pinned',
            (pinned) =>
              isPinned
                ? [...pinned.filter((i) => i !== diffColl), diffColl] // Add to end when pinning
                : pinned.filter((i) => i !== diffColl), // Remove when unpinning
          )
          .update('float', (float) => float.filter((i) => i !== diffColl)),
      orderedColumns,
    );

    this.onChangeOrder(newOrderedColumns);
  };

  onChangePinnedCols = (pinnedCols) => {
    const { updateJournalSettingsPinnedCols } = this.props;
    this.onUpdateOrderAfterChangePinnedCols(pinnedCols);
    updateJournalSettingsPinnedCols(pinnedCols);
  };

  onChangeDisabledCols = (disabledCols) => {
    const { updateJELineColumnsVisibility } = this.props;
    updateJELineColumnsVisibility(disabledCols);
  };

  onChangeLoadedVgridPages = (loadedVgridPages) => {
    this.setState({ loadedVgridPages });
  };

  onChangeOrder = (order) => {
    const { updateJournalSettingsOrderedCols } = this.props;
    this.setState({ cacheOrder: order });
    updateJournalSettingsOrderedCols(order);
  };

  get meta() {
    const { rtl, data } = this.props;
    return getMeta(data, rtl);
  }

  get gridMeta() {
    return this.getGridMetaData();
  }

  get settingsMeta() {
    return this.getSettingsMeta();
  }

  get combineMeta() {
    return this.getCombineMeta();
  }

  get pinnedCols() {
    const { journalSettingsPinnedCols } = this.props;
    // If user has customized pinned columns, use their preference
    // Otherwise, use backend defaults (permanentlyPinned)
    return journalSettingsPinnedCols.length > 0 ? journalSettingsPinnedCols : this.permanentlyPinned;
  }

  get disabledCols() {
    const { journalSettingsDisabledCols } = this.props;
    return journalSettingsDisabledCols;
  }

  get permanentlyPinned() {
    return this.getPermanentlyPinned();
  }

  get visible() {
    if (this.refVisible && this.refVisible.length) {
      return this.refVisible.slice();
    }
    return undefined;
  }

  get visibleRowNumbers() {
    const { visible } = this;
    if (!visible) return undefined;

    const { settings } = this.props;
    const [start, end] = visible;
    const delta = end - start;
    const gridStart = start + settings.size + 0;

    return [...settings.toJS(), ...range(gridStart, gridStart + delta + 2)];
  }

  get visibleData() {
    const {
      visibleRowNumbers,
      meta: [cols],
    } = this;
    const { data, jeType } = this.props;
    if (isStatement(jeType)) {
      const keys = visibleRowNumbers.reduce((res, rowNum) => [...res, ...cols.map((c) => `${c}${rowNum}`)], []);
      return data.filter((v, k) => keys.includes(k));
    }
    return data;
  }

  get startGridRow() {
    const { lineItems } = this.props;
    return lineItems.reduce((res, val) => (typeof res === 'undefined' || res > val ? val : res), undefined);
  }

  get rowMessages() {
    const { rowMessages } = this.props;
    return rowMessages;
  }

  get cellHotkeys() {
    const { hotkeyList } = this.props;
    return hotkeyList.filter((keys) => keys.level === 'cell');
  }

  getCurrentOrder = () => {
    const { cacheOrder } = this.state;
    const { orderedColumns } = this.props;
    return cacheOrder || orderedColumns;
  };

  getCombineMeta = createMemoFn(
    () => {
      const [pCols] = this.getGridEnabledPinnedMeta();
      const [fCols, rows] = this.getGridEnabledFloatMeta();
      const { settingsMeta } = this;
      // Only reverse float columns, keep pinned columns in pin sequence order
      const orderedFloatCols = this.orderColumns(fCols);
      const orderedCols = [...pCols, ...orderedFloatCols];
      return { orderedCols, rows, settingsMeta };
    },
    ({ orderedCols, rows, settingsMeta }) => [settingsMeta, [orderedCols, rows]],
  );

  getPermanentlyPinned = createMemoFn(
    () => {
      const [cols] = this.gridMeta;
      const { pinnedColumn } = this.props;
      const orderedCols = this.orderColumns(cols)[0];
      return { orderedCols, pinnedColumn };
    },
    ({ orderedCols, pinnedColumn }) => {
      // Filter out duplicates: don't add columns from pinnedColumn if they're already in orderedCols
      const additionalPins = pinnedColumn.filter((col) => !orderedCols.includes(col));
      return [orderedCols, ...additionalPins];
    },
  );

  getSettingsMeta = createMemoFn(
    () => {
      const { rtl, rawSettingsData } = this.props;
      return { rtl, rawSettingsData };
    },
    ({ rtl, rawSettingsData }) => getMetaRange(rawSettingsData, rtl),
  );

  getGridMetaData = createMemoFn(
    () => {
      const { rtl, gridData } = this.props;
      return { rtl, gridData };
    },
    ({ rtl, gridData }) => getMetaRange(gridData, rtl),
  );

  getStoreKey = (props = this.props) => {
    const { userGUID, jeInvoiceType, erp } = props;
    return generateJETableStoreKey([userGUID, jeInvoiceType, erp]);
  };

  getGridEnabledPinnedMeta = () => {
    const [cols, rows] = this.getGridEnabledMeta();
    const orderedColumns = this.getCurrentOrder();

    const pinnedCols = this.extractPinnedCols(cols);
    const enablePinnedOrder = orderedColumns.pinned.filter((item) => item === '*' || pinnedCols.includes(item));
    return [orderByMap(pinnedCols, enablePinnedOrder), rows];
  };

  getGridEnabledFloatMeta = () => {
    const [cols, rows] = this.getGridEnabledMeta();
    const orderedColumns = this.getCurrentOrder();
    const floatCols = this.extractFloatCols(cols);
    const enableFloatOrder = orderedColumns.float.filter((item) => item === '*' || floatCols.includes(item));
    return [orderByMap(floatCols, enableFloatOrder), rows];
  };

  getGridEnabledMeta = createMemoFn(
    () => {
      const [cols, rows] = this.gridMeta;
      const allEnabled = this.getAllEnabledColumns();
      const orderedCols = this.orderColumns(cols.filter((col) => allEnabled.includes(col)));
      return { orderedCols, rows };
    },
    ({ orderedCols, rows }) => [orderedCols, rows],
  );

  // get settings and force enabled cell
  getAllEnabledColumns = createMemoFn(
    () => ({
      enableColumns: this.getEnabledColumns(),
      forceEnableColumns: this.getForcedEnabledColumns(),
    }),
    ({ enableColumns, forceEnableColumns }) => _.uniq([...enableColumns, ...forceEnableColumns]),
  );

  getEnabledColumns = createMemoFn(
    () => {
      const [columns] = this.gridMeta;

      return { columns, disabledCols: this.disabledCols };
    },
    ({ columns, disabledCols }) => columns.filter((c) => !disabledCols.includes(c)),
  );

  getForcedEnabledColumns = createMemoFn(
    () => {
      const [columns] = this.gridMeta;
      return { columns };
    },
    ({ columns }) => columns.filter(this.isForcedEnabledCol),
  );

  getItem = (floatOptions, stringOptions) => (data) => {
    const {
      selectedRows,
      isJeLayoutRTL,
      reference,
      data: gridData,
      rtl,
      activeReference,
      setActive,
      polygons,
      documentView,
      showRowDetails,
      amountColumn,
      setEditing,
      endEditing,
      isJEEditing,
    } = this.props;
    const { active } = this.state;
    if (data && data instanceof Record) {
      if (data.type === 'description') {
        return <DescriptionCell data={data} />;
      }
      const cell = matchCell(data._cell);
      const optionList = data.type === 'float' ? floatOptions : stringOptions;

      return (
        <Cell
          gridData={gridData}
          data={data}
          key={data.type + data.readonly}
          rtl={rtl}
          isPriorityErp={isJeLayoutRTL}
          createRefernce={this.handleCreateReference}
          activeReference={activeReference}
          setActiveReference={setActive}
          polygons={polygons}
          optionList={optionList}
          active={active}
          onChange={this.handleChange}
          onActive={this.handleActive}
          documentView={documentView}
          isSupport={this.checkSupport(data)}
          insertGrid={this.insertGrid}
          setActiveCell={this.setActiveCell}
          isBulkEdit={selectedRows.has(cell[1])}
          onShowDetails={showRowDetails}
          amountColumn={amountColumn}
          reference={reference}
          hotkeyList={this.cellHotkeys}
          setEditing={setEditing}
          endEditing={endEditing}
          isJEEditing={isJEEditing}
        />
      );
    }
    return null;
  };

  getUpdateParams(cell, cellSet, isReference = false) {
    const { data, settingsData } = this.props;

    const item = resolveOriginalCell(cell, settingsData, data);
    if (item instanceof Record) {
      return item.set(
        'cellSet',
        // remove spaces in value for some fields if we set value via dnd
        isReference
          ? cellSet.set('value', this.removeSpacesByCellName(item.name, cellSet.value).substr(0, item.maxLength))
          : cellSet,
      );
    }
    return undefined;
  }

  getOptions = createMemoPropFn(() => {
    const { polygons } = this.props;
    const baseOptions = polygons.reduce((acc, polygon) => acc.add(polygon.text), new Set()).toList();

    return {
      stringOptions: this.getStringOptions(baseOptions),
      floatOptions: this.getFloatOptions(baseOptions),
    };
  }, ['polygons']);

  getFloatOptions = (options) =>
    options.reduce((list, text) => {
      const value = parseFloat(text);
      if (value.toString() === text) {
        return list.push(
          OptionItemFactory({
            id: text,
            value: text,
            display: text,
          }),
        );
      }
      return list;
    }, new List());

  getStringOptions = (options) =>
    options.map((text) =>
      OptionItemFactory({
        id: text,
        value: text,
        display: text,
      }),
    );

  getNextCell = getNext;

  getRowInVisibleArea = (currentRow) => {
    const [topRow, bottomRow] = this.visible;

    const insideTopRow = topRow + (this.startGridRow + 1);
    const insideBottomRow = bottomRow + (this.startGridRow - 2);

    const res = _.cond([
      [(r) => r < insideTopRow, _.constant(insideTopRow)],
      [(r) => r > insideBottomRow, _.constant(insideBottomRow)],
      [_.stubTrue, _.constant(currentRow)],
    ])(currentRow);
    return res;
  };

  getPageParams = () => {
    const [startRow, endRow] = this.visible ? this.visible : [0, 0];
    const loadPages = getLoadPagesByRows(startRow, endRow, 0);
    const { page, offset } = zipQueryParams(loadPages);
    return [loadPages, { page, pageSize: offset }];
  };

  setInitialFocus = () => {
    const { data } = this.props;

    const focusedRecord = data.find((cellRecord) => cellRecord.focused);
    if (typeof focusedRecord !== 'undefined') {
      this.setActiveCell(focusedRecord._cell);
    }
  };

  // eslint-disable-next-line max-len
  getEnterNextCell = (active) => {
    const { rtl, isJeLayoutRTL } = this.props;
    return this.getNextCell(13, rtl, isJeLayoutRTL, this.combineMeta, this.visibleData, active);
  };

  setActiveCell = (cellId) => {
    const { active } = this.state;
    if (cellId !== active) {
      this.setState({ active: cellId });
    }
  };

  setRefVisible = (ref) => {
    const { active } = this.state;
    this.refVisible = ref;
    if (active) {
      const [col, row] = matchCell(active);
      if (row >= this.startGridRow) {
        const visibleRow = this.getRowInVisibleArea(row);
        if (visibleRow !== row) {
          this.setActiveCell(`${col}${visibleRow}`);
        }
      }
    }
  };

  setLoadingAfterTimeout = (action, payload, timeout = 1000) => {
    let id;
    let loadingSet = false;
    const { setLoading, endLoading } = this.props;
    new Promise((resolve, reject) => {
      id = setTimeout(() => {
        loadingSet = true;
        setLoading();
      }, timeout);

      action({ ...payload, resolve, reject });
    })
      .catch((e) => e)
      .finally(() => {
        if (id) {
          clearTimeout(id);
        }
        if (loadingSet) {
          endLoading();
        }
      });
  };

  removeSpacesByCellName = (cellName, value) =>
    cellName.includes('priv_doc_finID') ? value.split(' ').join('') : value;

  extractFloatCols = (columns) => {
    const { pinnedCols } = this;
    return columns.filter((c) => !pinnedCols.includes(c));
  };

  extractPinnedCols = (columns) => {
    const { pinnedCols } = this;
    // Preserve user's pin order instead of original grid column order
    return pinnedCols.filter((c) => columns.includes(c));
  };

  isRequiredCell = (cellName) => {
    const { gridData } = this.props;
    const cellData = gridData.get(cellName);
    if (!cellData) return false;

    const { mandatory, readonly } = cellData;
    return mandatory && !readonly;
  };

  isForcedEnabledCol = (colName) => {
    const rows = _.tail(this.gridMeta[1]);
    return (
      this.pinnedCols.includes(colName) ||
      _.some(rows, (row) => {
        const cell = `${colName}${row}`;
        return this.isRequiredCell(cell);
      })
    );
  };

  orderColumns = (cols) => {
    const { rtl, isJeLayoutRTL } = this.props;
    return rtl !== isJeLayoutRTL ? [...cols].reverse() : cols;
  };

  insertGrid = ({ grid, polygons }, cell) => {
    const { documentID, jeType, insertGrid } = this.props;

    const [loadPages, pageParams, addLoadedPages] = isStatement(jeType)
      ? [...this.getPageParams(), this.onChangeLoadedVgridPages]
      : [null, {}, _.identity];

    addLoadedPages(loadPages);
    insertGrid({ polygons, grid, cell, documentID, pageParams });
  };

  clearLoadedVgridPages = () => {
    const { jeType } = this.props;
    if (isStatement(jeType)) {
      this.setState({ loadedVgridPages: [] });
    }
  };

  checkSupport = (data) => {
    const { reference, getCachedPaginationList } = this.props;
    return reference.size ? checkCallDataSupport(reference.first(), data, getCachedPaginationList) : undefined;
  };

  handleActive = (value, currentActive) => {
    const { active: stateActive } = this.state;
    const { rtl, isJeLayoutRTL } = this.props;
    const active = stateActive || currentActive;
    if (typeof value === 'string') {
      this.setActiveCell(value);
    } else if (typeof active === 'string' && (keyCodeArrow.has(value) || value === 13)) {
      const cell = this.getNextCell(value, rtl, isJeLayoutRTL, this.combineMeta, this.visibleData, active);
      this.setActiveCell(cell);
    }
  };

  handleChange = (cell, cellSet, ignoreSelectedRows = false, hotkeyMatch) => {
    const { documentID, onChange, jeType, selectedRows } = this.props;
    const rowsToSelect = ignoreSelectedRows ? new Set() : selectedRows;
    const item = this.getUpdateParams(cell, cellSet);
    const [loadPages, pageParams, addLoadedPages] = isStatement(jeType)
      ? [...this.getPageParams(), this.onChangeLoadedVgridPages]
      : [null, {}, _.identity];
    addLoadedPages(loadPages);

    if (item) {
      this.setLoadingAfterTimeout(onChange, {
        item,
        documentID,
        selectedRows: rowsToSelect,
        pageParams,
        ...hotkeyMatchAdapter(hotkeyMatch),
      });
      this.clearLoadedVgridPages();
    }
  };

  handleCreateReference = (params) => {
    const { polygons, extraPolygon, createReference, documentID, jeType, selectedRows } = this.props;
    const hangler = params.isMulti ? createMultiPolygonRefernce : createPolygonReferens;
    const { cell, cellSet } = hangler(params.cell, params.text, polygons, extraPolygon);

    // const appendedCellSet = this.getCreateReferenceValue(cell, cellSet);
    const item = this.getUpdateParams(cell, cellSet, true);
    const [loadPages, pageParams, addLoadedPages] = isStatement(jeType)
      ? [...this.getPageParams(), this.onChangeLoadedVgridPages]
      : [null, {}, _.identity];
    addLoadedPages(loadPages);

    if (item) {
      this.setLoadingAfterTimeout(createReference, {
        item,
        documentID,
        pageParams,
        visible: this.visible,
        selectedRows,
      });
    }
  };

  removeHandler = (r) => {
    const { documentID, onUpdate, data, selectedRows, selectRow } = this.props;
    const item = removeLine(r, data, selectedRows);
    const pageParams = this.getPageParams();

    if (item) {
      this.setLoadingAfterTimeout(onUpdate, { item, documentID, visible: this.visible, pageParams: pageParams[1] });
    }
    selectRow(new Set());
  };

  isReadOnly = () => {
    const { documentStatuses } = this.props;
    return documentStatuses.includes('accepted');
  };

  renderGrid() {
    // eslint-disable-next-line max-len
    const {
      data,
      className,
      jeType,
      lineCount,
      rtl,
      selectedRows,
      settingsData,
      viewArrangement,
      gridLineColumnsDictionary,
      activeReference,
      lineItems,
      gridData,
      getJournalEntryPage,
      initialHiddenColumns,
      reconcileColumn,
      notHiddenColumns,
      showReconciliationRequestDetails,
      hasVisibleColumnsManageButton,
      textractEnabledForDocument,
    } = this.props;

    const { active, loadedVgridPages } = this.state;
    const { floatOptions, stringOptions } = this.getOptions(this.props)();
    const orderedColumns = this.getCurrentOrder();

    const pinnedMeta = this.getGridEnabledPinnedMeta();
    // exclude first title row, and last empty rows.
    const countAllRows = isStatement(jeType) ? lineCount - 6 : lineCount - 2;
    const isReadOnly = this.isReadOnly();

    return data.size > 0 ? (
      <JERoot className={className}>
        <JEProcessingOverlay />
        <JEHeadersTable
          getItem={this.getItem(floatOptions, stringOptions)}
          active={active}
          activeReference={activeReference}
          data={settingsData}
          rawData={this.props.rawSettingsData}
        />

        <JELinesSelectAll>
          {!isReadOnly && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <SelectAllCheckbox
                  startRow={pinnedMeta[1][0]}
                  countAllRows={countAllRows}
                  selectedRows={selectedRows}
                  handleChange={this.onChangeSelectedRows}
                />

                {textractEnabledForDocument && (
                  <SelectUnmatchedLinesCheckbox handleChange={this.onSelectUnmatchedLines} />
                )}
              </Box>
            </>
          )}
        </JELinesSelectAll>

        {isStatement(jeType) ? (
          <JEVirtualLinesTable
            onRowsRender={this.setRefVisible}
            meta={this.gridMeta}
            lineItems={lineItems}
            getItem={this.getItem(floatOptions, stringOptions)}
            data={gridData}
            active={active ? matchCell(active) : [null, null]}
            getJournalEntry={getJournalEntryPage}
            removeHandler={this.removeHandler}
            rtl={rtl}
            lineCount={lineCount}
            storeKey={this.getStoreKey()}
            initialHiddenColumns={initialHiddenColumns}
            selectedRows={selectedRows}
            onChangeSelectedRows={this.onChangeSelectedRows}
            countAllRows={countAllRows}
            isReadOnly={isReadOnly}
            loadedPages={loadedVgridPages}
            onChangeLoadedPages={this.onChangeLoadedVgridPages}
            reconcileColumn={reconcileColumn}
            notHiddenColumns={notHiddenColumns}
            forcedEnabledCols={this.getForcedEnabledColumns(this.gridMeta[0])}
            rowMessagesRelativeCol={reconcileColumn}
            rowMessages={this.rowMessages}
            googleSearchCols={['B']}
            onRequestIndicatorClick={showReconciliationRequestDetails}
            gridLineColumnsDictionary={gridLineColumnsDictionary}
          />
        ) : (
          <JELinesTable
            data={gridData}
            getItem={this.getItem(floatOptions, stringOptions)}
            active={active}
            removeHandler={this.removeHandler}
            activeReference={activeReference}
            pinnedCols={this.pinnedCols}
            disabledCols={this.disabledCols}
            meta={this.gridMeta}
            lineItems={lineItems}
            pinnedMeta={pinnedMeta}
            floatMeta={this.getGridEnabledFloatMeta()}
            enabledCols={this.getAllEnabledColumns()}
            forcedEnabledCols={this.getForcedEnabledColumns(this.gridMeta[0])}
            permanentlyPinned={this.permanentlyPinned}
            onChangePinnedCols={this.onChangePinnedCols}
            onChangeDisabledCols={this.onChangeDisabledCols}
            storeKey={this.getStoreKey()}
            selectedRows={selectedRows}
            onChangeSelectedRows={this.onChangeSelectedRows}
            countAllRows={countAllRows}
            isReadOnly={isReadOnly}
            viewArrangement={viewArrangement}
            gridLineColumnsDictionary={gridLineColumnsDictionary}
            onChangeOrder={this.onChangeOrder}
            order={orderedColumns}
            hasVisibleColumnsManageButton={hasVisibleColumnsManageButton}
          />
        )}
        {jeType === 'default' ? (
          <JESummaryTable
            getItem={this.getItem(floatOptions, stringOptions)}
            active={active}
            activeReference={activeReference}
          />
        ) : null}
        {jeType === 'manual_je' ? (
          <JEManualSummaryTable
            getItem={this.getItem(floatOptions, stringOptions)}
            active={active}
            activeReference={activeReference}
          />
        ) : null}
      </JERoot>
    ) : null;
  }

  renderError() {
    const { errorFromErp } = this.props;

    return errorFromErp ? <JEError /> : null;
  }

  render() {
    const { isJELoading } = this.props;
    return (
      <>
        {isJELoading && (
          <JELoader>
            <CircularProgress />
          </JELoader>
        )}
        <LineHotkeyCatcher handleHotkeyMatch={this.handleChange} />
        <OptionalLineHotkeyCatcher handleHotkeyMatch={this.handleChange} />
        <DocumentHotkeyCatcher handleHotkeyMatch={this.handleChange} />
        {this.renderGrid() || this.renderError()}
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  activeReference: activeReferenceSelector(state),
  data: journalSelector(state),
  gridData: gridEntriesSelector(state),
  lineItems: lineItemsSelector(state),
  lines: linesSelector(state),
  settings: settingsSelector(state),
  settingsData: rearrangedSettingsSelector(state),
  rawSettingsData: settingsEntriesSelector(state),
  polygons: polygonsSelector(state),
  rtl: rtlEnable(state),
  documentView: documentViewSelector(state),
  userGUID: userGUIDSelector(state),
  extraPolygon: extraPolygonSelector(state),
  reference: referenceSelector(state),
  jeType: jeTypeSelector(state),
  initialHiddenColumns: initialHiddenColumnsSelector(state),
  jeInvoiceType: jeInvoiceTypeSelector(state),
  erp: currentConnectedERPselector(state),
  lineCount: lineCountSelector(state),
  documentStatuses: documentStatusesSelector(state),
  selectedRows: selectedRowSelector(state),
  reconcileColumn: reconcileColumnSelector(state),
  notHiddenColumns: notHiddenColumnsSelector(state),
  amountColumn: amountColumnSelector(state),
  rowMessages: rowMessagesSelector(state),
  errorFromErp: errorFromErpSelector(state),
  pinnedColumn: pinnedColumnSelector(state),
  orderedColumns: journalSettingsOrderedColsSelector(state),
  isJeLayoutRTL: isJeLayoutRTLSelector(state),
  journalSettingsDisabledCols: journalSettingsDisabledColsSelector(state),
  journalSettingsPinnedCols: journalSettingsPinnedColsSelector(state),
  hotkeyList: documentHotkeysSelector(state),
  getCachedPaginationList: getCachedPaginationListSelector(state),
  gridLineColumnsDictionary: jeGridLineColumnsDictionarySelector(state),
  isJELoading: isJELoadingSelector(state),
  isJEEditing: isJEEditingSelector(state),
  textractEnabledForDocument: textractEnabledForDocumentSelector(state),
});

const mapDispatchToProps = {
  createReference: createReferenceAction,
  onChange: updateJournalEntryAction,
  getJournalEntryPage: getJournalEntryPageAction,
  setActive: setRefernceAction,
  insertGrid: insertGridAction,
  onUpdate: updateJournalEntriesAction,
  selectRow: selectRowAction,
  showRowDetails: showRowDetailsAction,
  showReconciliationRequestDetails: showReconciliationRequestDetailsAction,
  getJELineColumnsVisibility: getJELineColumnsVisibilityAction,
  getJournalSettingsPinnedCols: getJournalSettingsPinnedColsAction,
  getJournalSettingsOrderedCols: getJournalSettingsOrderedColsAction,
  updateJournalSettingsPinnedCols: updateJournalSettingsPinnedColsAction,
  updateJELineColumnsVisibility: updateJELineColumnsVisibilityAction,
  updateJournalSettingsOrderedCols: updateJournalSettingsOrderedColsAction,
  setLoading: setLoadingAction,
  endLoading: endLoadingAction,
  setEditing: setEditingAction,
  endEditing: endEditingAction,
  selectUnmatchedLines: selectUnmatchedLinesAction,
};

export default compose(connect(mapStateToProps, mapDispatchToProps))(Tables);
